import React, { useState } from 'react';
import QRCode from 'react-qr-code';
import { FaLayerGroup, FaReceipt, FaUser } from 'react-icons/fa';
import './MembershipCard.css';
import API_BASE_URL from '../../config/api';

/** Maps /api/payment/quota data to the card's payment state: 'paid' | 'pending' | 'overdue' | null */
export const getCardPaymentState = (quotaStatus, overdueMonths = []) => {
    if (!quotaStatus || quotaStatus === '-') return null;
    if (quotaStatus === 'Regularizada' && overdueMonths.length === 0) return 'paid';
    if (quotaStatus === 'Pendente' && overdueMonths.length === 0) return 'pending';
    return 'overdue';
};

const PAYMENT_LABELS = {
    paid: { label: 'Pagamento em dia', icon: 'fa-check-circle' },
    pending: { label: 'Pagamento pendente', icon: 'fa-clock' },
    overdue: { label: 'Pagamento em atraso', icon: 'fa-exclamation-circle' },
};

const MembershipCard = ({
    memberNumber = '0000',
    name = 'Nome do Sócio',
    memberSince = '—',
    status = 'Ativo',
    sport = 'Basquetebol',
    cardType = 'atleta',
    userId = null,
    compact = false,
    photoUrl = null,
    paymentState = null,
}) => {
    const [downloading, setDownloading] = useState(false);
    const [photoError, setPhotoError] = useState(false);

    const isAtleta = cardType === 'atleta';

    const photoSrc = photoUrl
        ? (photoUrl.startsWith('http') || photoUrl.startsWith('data:') ? photoUrl : `${API_BASE_URL}${photoUrl}`)
        : null;

    const statusLabel =
        status === 'Active' ? 'Ativo' :
            status === 'Pending' ? 'Pendente' :
                status === 'Suspended' ? 'Suspenso' :
                    status === 'Cancelled' ? 'Cancelado' : status;

    const statusClass =
        (status === 'Ativo' || status === 'Active') ? 'status-active' :
            (status === 'Pendente' || status === 'Pending') ? 'status-pending' :
                'status-inactive';

    const numDisplay = memberNumber?.toString().padStart(6, '0') ?? '000000';

    const handleDownload = async (e) => {
        e.stopPropagation();
        if (!userId) return;
        setDownloading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${API_BASE_URL}/api/membershipcard/download?userId=${userId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `Cartao_Socio_${numDisplay}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            }
        } catch (err) {
            console.error('Download failed', err);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className={`mc-wrapper${compact ? ' mc-wrapper--compact' : ''}`}>
            {/* ══════ CARTÃO (face única) ══════ */}
            <div className={`mc-card${paymentState === 'overdue' ? ' mc-card--overdue' : ''}`}>
                {/* Decoração de fundo */}
                <div className="mc-deco-circle mc-deco-circle--1" />
                <div className="mc-deco-circle mc-deco-circle--2" />
                <div className="mc-holo-overlay" />

                {/* Linha 1 – Logo + tipo + estado */}
                <div className="mc-row mc-row--top">
                    <div className="mc-logo-box">
                        <img src="/CDP_logo.png" alt="CDP" className="mc-logo-img" />
                    </div>
                    <div className="mc-row-spacer" />
                    <span className={`mc-badge ${statusClass}`}>
                        <span className="mc-badge-dot" />
                        {statusLabel}
                    </span>
                    <span className="mc-type-pill">
                        {isAtleta ? <><FaLayerGroup /> ATLETA</> : <><FaReceipt /> SOCIO</>}
                    </span>
                </div>

                {/* Corpo: foto à esquerda + info ao centro + QR à direita */}
                <div className="mc-body-wrapper">
                    <div className="mc-photo-section">
                        {photoSrc && !photoError ? (
                            <img
                                src={photoSrc}
                                alt={name}
                                className="mc-photo-img"
                                onError={() => setPhotoError(true)}
                            />
                        ) : (
                            <div className="mc-photo-placeholder" title="Sem foto de perfil">
                                <FaUser />
                            </div>
                        )}
                    </div>

                    <div className="mc-info-side">
                        {/* Número */}
                        <div className="mc-number-block">
                            <span className="mc-number-prefix">Nº</span>
                            <span className="mc-number-value">{numDisplay}</span>
                        </div>

                        {/* Campos */}
                        <div className="mc-fields-row">
                            <div className="mc-field">
                                <span className="mc-field-label">TITULAR</span>
                                <span className="mc-field-value">{name}</span>
                            </div>
                            <div className="mc-field">
                                <span className="mc-field-label">DESDE</span>
                                <span className="mc-field-value">{memberSince}</span>
                            </div>
                        </div>

                        {paymentState && (
                            <span className={`mc-payment mc-payment--${paymentState}`}>
                                <i className={`fas ${PAYMENT_LABELS[paymentState].icon}`} />
                                {PAYMENT_LABELS[paymentState].label}
                            </span>
                        )}
                    </div>

                    {/* QR Code section */}
                    <div className="mc-qr-section">
                        <div className="mc-qr-container">
                            <QRCode
                                value={`${(statusLabel.toLowerCase() === 'ativo' || statusLabel.toLowerCase() === 'active') ? '✅' : '❌'} SÓCIO: ${statusLabel.toUpperCase()}\n👤 Nome: ${name}\n🆔 Nº Sócio: ${numDisplay}${paymentState ? `\n💶 Quotas: ${paymentState === 'paid' ? '✅ EM DIA' : paymentState === 'pending' ? '⏳ PENDENTE' : '❌ EM ATRASO'}` : ''}`}
                                size={120}
                                style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                                bgColor="transparent"
                                fgColor="#ffffff"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Botão abaixo do cartão */}
            <div className="mc-actions">
                {userId && (
                    <button
                        className="mc-download-btn"
                        onClick={handleDownload}
                        disabled={downloading}
                    >
                        {downloading
                            ? <><i className="fas fa-spinner fa-spin" /> A gerar...</>
                            : <><i className="fas fa-download" /> Descarregar PDF</>}
                    </button>
                )}
            </div>
        </div>
    );
};

export default MembershipCard;
